import { BADGE_MAX, STAFF_BADGE_ICONS, isBadge } from "odyssey-shared";
import type { IconArt, StaffBadgeBox } from "../../ui/kit/index.ts";
import { iconArt, type FallbackLog, type IconsSnapshot } from "../icons/art.ts";

/**
 * The look of a staff badge (ADR-112 decisions 4.2, 4.3 and 4.7): for each badge number from 1 to 15 its atlas key,
 * the type it names and whether it is a leader's (which the pack's gold art shows). Pure: the words are `words.ts`'s and the drawing is the kit's `StaffBadge`.
 */

/**
 * The staff type a badge names, as the language files key its word.
 */
export type BadgeType = "administrator" | "support" | "events" | "builder" | "designer" | "guideLead" | "guide";

/**
 * What a badge looks like.
 *
 * `leader` is true for the five gold badges that have a white sibling.
 */
export interface BadgeLook {
  readonly badge: number;
  readonly key: string;
  readonly type: BadgeType;
  readonly leader: boolean;
}

/**
 * The type of each badge number from 1 to 15, with whether the badge is its type's leader's.
 */
const TYPE_OF_BADGE: Readonly<Record<number, readonly [BadgeType, boolean]>> = {
  1: ["guide", false],
  2: ["guide", false],
  3: ["guide", false],
  4: ["guide", false],
  5: ["guideLead", false],
  6: ["support", false],
  7: ["support", true],
  8: ["events", false],
  9: ["events", true],
  10: ["builder", false],
  11: ["builder", true],
  12: ["administrator", false],
  13: ["administrator", true],
  14: ["designer", false],
  15: ["designer", true],
};

/**
 * The look of each badge number from 1 to 15, made once, so asking for one allocates nothing and a plate can compare
 * looks by identity.
 */
const LOOKS: readonly (BadgeLook | null)[] = Object.freeze(
  Array.from({ length: BADGE_MAX + 1 }, (_, badge): BadgeLook | null => {
    const entry = TYPE_OF_BADGE[badge];
    const key = STAFF_BADGE_ICONS[badge];
    if (entry === undefined || key === undefined) {
      return null;
    }
    const [type, leader] = entry;
    return Object.freeze({ badge, key, type, leader });
  }),
);

/**
 * The look of a badge.
 *
 * @param badge A badge number, as the status byte carries it.
 * @returns Its look, the same object each time for a number, or null for 0 (none) and for anything that is not a whole
 *   number from 0 to 15, so a badge the page does not know draws nothing.
 * @example
 * badgeLook(7)?.leader;
 * // true: Player Support's leader, framed
 */
export function badgeLook(badge: number): BadgeLook | null {
  return isBadge(badge) ? (LOOKS[badge] ?? null) : null;
}

/**
 * The art a badge is drawn with in a box, or null for its glyph. The art is drawn at every UI scale and in every box, whole
 * where a whole number of device pixels fits the art's visible 24 pixels and downscaled to the box where none does
 * (`badgeArtUnit`). There is no glyph look: a badge with no art to draw is not drawn (ADR-112 decision 4.3).
 *
 * @param snapshot The atlas's state and the UI scale.
 * @param look The badge's look.
 * @param box The box the badge is drawn in.
 * @param log Where a fallback is said, once per topic.
 * @returns The picture, or null while the atlas has not decoded or failed, or when the pointer lacks the key, which
 *   draws no badge until the art arrives.
 */
export function staffBadgeArt(snapshot: IconsSnapshot, look: BadgeLook, box: StaffBadgeBox, log: FallbackLog): IconArt | null {
  return iconArt(snapshot, look.key, `badge ${look.badge}`, box, log, { needsFit: false, instead: "no badge is drawn" });
}

/**
 * How a plate's badge is drawn, as the one value a plate compares to know it must paint the badge and lay out again:
 * empty for no badge or no art yet, and the art's frame name where it is drawn. A plate repaints in the frame this
 * changes, when the badge arrives or goes or its art arrives, and in no other.
 *
 * @param look The badge's look, or null for none.
 * @param artFrame The icon atlas frame its art draws, or null while there is none.
 * @returns The value.
 */
export function badgeDrawnKey(look: BadgeLook | null, artFrame: string | null): string {
  return look === null || artFrame === null ? "" : artFrame;
}
